import { logger } from '@/lib/telemetry/logger';
import type { CanonicalImageMediaType, StudioFailure, StudioFailureCode } from './types';

export const STUDIO_FAILURE_CODES: readonly StudioFailureCode[] = [
  'source_too_large',
  'unsupported_media',
  'decode_failed',
  'sample_failed',
  'clipboard_read_unavailable',
  'clipboard_read_denied',
  'clipboard_no_supported_image',
  'clipboard_read_failed',
  'clipboard_write_unavailable',
  'clipboard_write_denied',
  'clipboard_write_unsupported_png',
  'clipboard_write_failed',
  'invalid_configuration',
  'unsupported_configuration_version',
  'unsupported_configuration_reference',
  'canvas_allocation_failed',
  'render_failed',
  'png_encode_failed',
  'download_dispatch_failed',
];

export type StudioOperation =
  | 'import'
  | 'clipboard_read'
  | 'preview'
  | 'download'
  | 'clipboard_write'
  | 'configuration_apply';

export interface StudioDiagnosticContext {
  readonly operation: StudioOperation;
  readonly mode?: 'generative' | 'image-frame' | 'mosaic';
  readonly width?: number;
  readonly height?: number;
  readonly mediaType?: CanonicalImageMediaType;
}

export interface StudioFailureRecord {
  readonly name: `pattern_studio.${string}_failed`;
  readonly level: 'warn' | 'error';
  readonly context: Record<string, string | number>;
}

const ERROR_CODES: ReadonlySet<StudioFailureCode> = new Set<StudioFailureCode>([
  'decode_failed',
  'sample_failed',
  'canvas_allocation_failed',
  'render_failed',
  'png_encode_failed',
  'download_dispatch_failed',
]);

export function mapStudioFailureRecord(
  failure: StudioFailure,
  source: StudioDiagnosticContext,
): StudioFailureRecord {
  const context: Record<string, string | number> = {
    code: failure.code,
    operation: source.operation,
  };
  if (source.mode !== undefined) context.mode = source.mode;
  if (source.width !== undefined) context.width = source.width;
  if (source.height !== undefined) context.height = source.height;
  if (source.mediaType !== undefined) context.mediaType = source.mediaType;
  if (failure.domExceptionName !== undefined) context.domExceptionName = failure.domExceptionName;
  return {
    name: `pattern_studio.${operationRecordStem(source.operation)}_failed`,
    level: ERROR_CODES.has(failure.code) ? 'error' : 'warn',
    context,
  };
}

export function emitStudioFailure(failure: StudioFailure, context: StudioDiagnosticContext): void {
  const record = mapStudioFailureRecord(failure, context);
  logger[record.level](record.name, record.context);
}

function operationRecordStem(operation: StudioOperation): string {
  if (operation === 'configuration_apply') return 'configuration_apply';
  return operation;
}
